import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ShieldCheck, ChevronDown, Info, Check, X, FileDown } from "lucide-react";

const fmtINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

// Plan family display order + customer-friendly benefit comparison (§22/§23)
const PLAN_GROUPS = [
  { code: "EHS", label: "Extended Hardware Service (EHS)",
    benefits: [["Hardware / mechanical coverage", true], ["Battery wear", false],
               ["Accidental damage", false], ["Enhanced Plus service", false]] },
  { code: "EHS_PLUS", label: "Extended Hardware Service Plus (EHS Plus)",
    benefits: [["Hardware / mechanical coverage", true], ["Battery wear", false],
               ["Accidental damage", false], ["Enhanced Plus service", true]] },
  { code: "CFB", label: "Complete for Business (CFB)",
    benefits: [["Hardware / mechanical coverage", true], ["Battery wear", true],
               ["Accidental damage", true], ["Enhanced Plus service", false]] },
  { code: "CFB_PLUS", label: "Complete for Business Plus (CFB Plus)",
    benefits: [["Hardware / mechanical coverage", true], ["Battery wear", true],
               ["Accidental damage", true], ["Enhanced Plus service", true]] },
];

/**
 * Non-intrusive protection recommendation for a quote line (Microsoft Surface warranty).
 * Groups eligible plans by family with 2 / 3 / 4-year tiers; default = Standard warranty only.
 * NEVER auto-adds a paid plan (§22/§23/§24).
 */
export const ProtectionOptions = ({ productCode, quantity = 1, onSelectPlan }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState("STANDARD");

  useEffect(() => {
    if (!productCode) return;
    let ok = true;
    api.get(`/warranty-plans/recommend-by-code/${encodeURIComponent(productCode)}`)
      .then(({ data }) => ok && setData(data)).catch(() => {});
    return () => { ok = false; };
  }, [productCode]);

  if (!data?.eligible) return null;

  const options = data.options || [];
  const selected = options.find((o) => o.id === choice);

  const downloadComparison = async () => {
    try {
      const { data: blob } = await api.get(
        `/warranty-plans/comparison-pdf?code=${encodeURIComponent(productCode)}`,
        { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { /* non-blocking */ }
  };

  return (
    <div className="mt-2 rounded-lg border border-sky-200 bg-sky-50/60" data-testid={`protection-${productCode}`}>
      <button onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left"
        data-testid={`protection-toggle-${productCode}`}>
        <ShieldCheck className="h-4 w-4 text-sky-600 shrink-0" />
        <span className="text-[13px] font-medium text-sky-800">Protect this device — Microsoft protection options available</span>
        <ChevronDown className={"h-4 w-4 text-sky-500 ml-auto transition-transform " + (open ? "rotate-180" : "")} />
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-2" data-testid={`protection-panel-${productCode}`}>
          <label className="flex items-center gap-2 text-[13px] text-slate-700 cursor-pointer py-1">
            <input type="radio" name={`prot-${productCode}`} checked={choice === "STANDARD"}
              onChange={() => setChoice("STANDARD")} data-testid={`protection-standard-${productCode}`} />
            <span className="font-medium">Standard device warranty only</span>
            <span className="text-slate-400 text-[11px]">(no paid plan)</span>
          </label>

          {PLAN_GROUPS.map((g) => {
            const groupOpts = options.filter((o) => o.plan_code === g.code)
              .sort((a, b) => (a.total_years || 0) - (b.total_years || 0));
            if (!groupOpts.length) return null;
            return (
              <div key={g.code} className="rounded-md border border-slate-200 bg-white"
                data-testid={`protection-group-${g.code}`}>
                <div className="px-2.5 py-1.5 border-b border-slate-100">
                  <div className="text-[12px] font-semibold text-slate-800">{g.label}</div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                    {g.benefits.map(([txt, on]) => (
                      <span key={txt} className={"inline-flex items-center gap-1 text-[10.5px] " + (on ? "text-emerald-700" : "text-slate-400")}>
                        {on ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />} {txt}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="px-2.5 py-1.5 space-y-1">
                  {groupOpts.map((o) => (
                    <label key={o.id} className="flex items-center gap-2 text-[13px] text-slate-700 cursor-pointer"
                      data-testid={`protection-opt-${o.part_number}`}>
                      <input type="radio" name={`prot-${productCode}`} checked={choice === o.id}
                        onChange={() => setChoice(o.id)} />
                      <span className="font-medium">{o.total_years} Years Total</span>
                      <span className="text-slate-400 text-[11px]">{o.part_number}</span>
                      <span className="font-mono text-slate-900 ml-auto">{fmtINR(o.price)} each</span>
                      {o.tax_status === "REVIEW_REQUIRED" && (
                        <span className="text-[10px] text-amber-600">(tax review)</span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            );
          })}

          <div className="flex items-center gap-1 text-[11px] text-slate-500 pt-0.5">
            <Info className="h-3 w-3 shrink-0" /> India-only national warranty · GST 18% · plans are a paid add-on, never added automatically
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              disabled={choice === "STANDARD"}
              onClick={() => selected && onSelectPlan?.({ ...selected, quantity })}
              data-testid={`protection-add-${productCode}`}
              className={"mt-1 text-[12px] font-medium rounded-md px-3 py-1.5 " + (choice === "STANDARD"
                ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                : "bg-sky-600 text-white hover:bg-sky-700")}>
              Add selected plan{selected ? ` · ${fmtINR((selected.price || 0) * quantity)}` : ""}
            </button>
            <button onClick={downloadComparison} data-testid={`protection-compare-${productCode}`}
              className="mt-1 inline-flex items-center gap-1.5 text-[12px] font-medium rounded-md px-3 py-1.5 border border-sky-300 text-sky-700 hover:bg-sky-50">
              <FileDown className="h-3.5 w-3.5" /> Plan comparison (PDF)
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
